import { Checkbox, IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { useHistory, useRouteMatch } from "react-router-dom"

import { useTranslation } from "react-i18next"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"
import type { PagingOptions } from "../../../../../models"
import { changeMetadataPage, deleteMetadata, fetchAllMetadata } from "../../../../../redux"
import type { AppState, AppThunkDispatch, Metadata, MetadataActions } from "../../../../../redux"
import {
  CoMaPagingBar,
  ContentManagerOverviewHeader,
  CrudDeleteDialog,
  CrudTableHeader,
  PageContainer
} from "../../../../molecules"

export interface IMapMetadataManagerOverviewPageProps {
  metadataList: Metadata[]
  pagingOptions: PagingOptions
  fetchMapMetadataList: () => void
  deleteMetadata: (id: string) => void
  changePage: (page: number) => void
}

function MapMetadataManagerOverviewPageView(props: IMapMetadataManagerOverviewPageProps): ReactElement {
  const { metadataList, pagingOptions, fetchMapMetadataList, deleteMetadata, changePage } = props

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Map)

  const { url } = useRouteMatch()
  const history = useHistory()
  const { t } = useTranslation("contentManager")

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined)

  useEffect(() => {
    fetchMapMetadataList()
  }, [fetchMapMetadataList])

  function navigateToEditForm(id: string): void {
    history.push(`${url}/${id}`)
  }

  function onPageChange(page: number): void {
    if (page < 0 || page >= pagingOptions.totalPages || page === pagingOptions.currentPage) {
      return
    }
    changePage(page)
  }

  function onDeleteButtonClicked(id: string) {
    setDialogOpen(true)
    setIdToRemove(id)
  }

  function onDialogClose(): void {
    setDialogOpen(false)
    setIdToRemove(undefined)
  }

  function onDeleteConfirm(): void {
    if (idToRemove) deleteMetadata(idToRemove)
    onDialogClose()
  }

  return (
    <PageContainer id="MapMetadataManagerOverviewPage" noPadding>
      <ContentManagerOverviewHeader onCreateButtonClicked={() => navigateToEditForm("create")} />
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <TableContainer>
        <Table>
          <CrudTableHeader
            headers={[
              {
                key: "pageNumber",
                label: t("metadata.table.pageNumber"),
                enableFiltering: true
              },
              {
                key: "title",
                label: t("metadata.table.mapName"),
                enableFiltering: true
              },
              {
                key: "isDigitalOnly",
                label: t("metadata.table.digital")
              }
            ]}
            onChange={fetchMapMetadataList}
          />
          <TableBody>
            {metadataList.map((metadata: Metadata, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <TableRow key={index}>
                <TableCell>{metadata.pageNumber}</TableCell>
                <TableCell>{metadata.displayTitle}</TableCell>
                <TableCell padding="checkbox">
                  <Checkbox checked={metadata.isDigitalOnly} disabled={true} />
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => navigateToEditForm(metadata.id as string)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onDeleteButtonClicked(metadata.id as string)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <CrudDeleteDialog open={dialogOpen} onClose={onDialogClose} onConfirm={onDeleteConfirm} />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    metadataList: state.metadata.metadataList,
    pagingOptions: state.metadata.pagingOptions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MetadataActions>) {
  return {
    fetchMapMetadataList: () => dispatch(fetchAllMetadata()),
    deleteMetadata: (id: string) => dispatch(deleteMetadata(id)),
    changePage: (page: number) => dispatch(changeMetadataPage(page))
  }
}

export const MapMetadataManagerOverviewPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(MapMetadataManagerOverviewPageView)
